import type {
  StoreWebsiteDocument,
  WebsiteIssueCode,
  WebsiteLinkValue,
  WebsiteMediaFraming,
  WebsiteMediaValue,
  WebsiteRichTextDocument,
  WebsiteSlotSchema,
  WebsiteSlotValue,
  WebsiteTemplateManifest,
  WebsiteThemeOverride,
} from '../../types/website-templates';
import {
  WEBSITE_CONTACT_METHODS,
  WEBSITE_INTERFACE_LOCALES,
  WEBSITE_MEDIA_KINDS,
  WEBSITE_STORE_PAGE_LINKS,
} from '../../types/website-templates';
import {
  WEBSITE_COLLECTION_REF_RE,
  WEBSITE_DOCUMENT_MAX_BYTES,
  WEBSITE_SLOT_ID_MAX_LENGTH,
  WEBSITE_SLOT_ID_RE,
  WEBSITE_UUID_RE,
  checkClosedKeys,
  isOneOf,
  isRecord,
  isStructuralKey,
  stableSlotJson,
  utf8ByteLength,
  type UnknownRecord,
} from './internal';
import { sanitizeStorefrontNewsletterCollectionConfig } from '../storefront-engine/content';
import { isAllowedSocialUrl, websiteAllowedLinkKinds } from './external-url';
import { validateWebsiteMediaFraming } from './media-frames';
import { WEBSITE_MEDIA_ALT_MAX_LENGTH } from './media-policy';
import { checkMerchantText } from './text-safety';
import { validateThemeOverride } from './theme';

export type SlotValidation =
  | { readonly ok: true; readonly value: WebsiteSlotValue }
  | { readonly ok: false; readonly code: WebsiteIssueCode };

export const WEBSITE_RICH_TEXT_BLOCK_LIMIT = 12;
export const WEBSITE_RICH_TEXT_LIST_ITEM_LIMIT = 12;
export const WEBSITE_RICH_TEXT_INLINE_LIMIT = 80;
const RICH_TEXT_MARKS = ['bold', 'italic'] as const;

function fail(code: WebsiteIssueCode): SlotValidation {
  return { ok: false, code };
}

type RichTextState = { length: number; visible: boolean; code: WebsiteIssueCode | null };

function failRichText(state: RichTextState, code: WebsiteIssueCode): null {
  // structure_override outranks every other finding for the same value.
  if (state.code !== 'structure_override') state.code = code;
  return null;
}

function validateRichTextInline(node: unknown, state: RichTextState): UnknownRecord | null {
  if (!isRecord(node)) return failRichText(state, 'invalid_slot');
  if (node.type === 'softBreak') {
    const issue = checkClosedKeys(node, ['type']);
    if (issue) return failRichText(state, issue);
    state.length += 1;
    return { type: 'softBreak' };
  }
  if (node.type !== 'text') {
    // Links and any other inline node are not part of the website grammar.
    const issue = checkClosedKeys(node, ['type', 'text', 'marks'], ['type']);
    return failRichText(state, issue === 'structure_override' ? issue : 'invalid_slot');
  }
  const issue = checkClosedKeys(node, ['type', 'text', 'marks'], ['type', 'text']);
  if (issue) return failRichText(state, issue);

  const text = checkMerchantText(node.text, {
    maxLength: Number.MAX_SAFE_INTEGER,
    allowBlank: true,
  });
  if (!text.ok) return failRichText(state, text.code);
  state.length += text.length;
  if (text.visible) state.visible = true;

  if (node.marks === undefined) return { type: 'text', text: node.text };
  if (!Array.isArray(node.marks) || node.marks.length === 0 || node.marks.length > 2) {
    return failRichText(state, 'invalid_slot');
  }
  const marks: string[] = [];
  for (const mark of node.marks) {
    if (!isOneOf(RICH_TEXT_MARKS, mark) || marks.includes(mark)) {
      return failRichText(state, 'invalid_slot');
    }
    marks.push(mark);
  }
  return { type: 'text', text: node.text, marks };
}

function validateRichTextInlines(
  value: unknown,
  state: RichTextState,
  minimum: number,
): UnknownRecord[] | null {
  if (
    !Array.isArray(value) ||
    value.length < minimum ||
    value.length > WEBSITE_RICH_TEXT_INLINE_LIMIT
  ) {
    return failRichText(state, 'invalid_slot');
  }
  const children: UnknownRecord[] = [];
  for (const child of value) {
    const node = validateRichTextInline(child, state);
    if (!node) return null;
    children.push(node);
  }
  return children;
}

function validateRichTextBlock(node: unknown, state: RichTextState): UnknownRecord | null {
  if (!isRecord(node)) return failRichText(state, 'invalid_slot');
  if (node.type === 'paragraph') {
    const issue = checkClosedKeys(node, ['type', 'children']);
    if (issue) return failRichText(state, issue);
    const children = validateRichTextInlines(node.children, state, 0);
    return children ? { type: 'paragraph', children } : null;
  }
  if (node.type === 'list') {
    // `style` is the list style here, never CSS.
    const issue = checkClosedKeys(node, ['type', 'style', 'items']);
    if (issue) return failRichText(state, issue);
    if (node.style !== 'unordered' && node.style !== 'ordered') {
      return failRichText(state, 'invalid_slot');
    }
    if (
      !Array.isArray(node.items) ||
      node.items.length === 0 ||
      node.items.length > WEBSITE_RICH_TEXT_LIST_ITEM_LIMIT
    ) {
      return failRichText(state, 'invalid_slot');
    }
    const items: UnknownRecord[] = [];
    for (const item of node.items) {
      if (!isRecord(item) || item.type !== 'listItem') return failRichText(state, 'invalid_slot');
      const itemIssue = checkClosedKeys(item, ['type', 'children']);
      if (itemIssue) return failRichText(state, itemIssue);
      const children = validateRichTextInlines(item.children, state, 1);
      if (!children) return null;
      items.push({ type: 'listItem', children });
    }
    return { type: 'list', style: node.style, items };
  }
  const issue = checkClosedKeys(node, ['type'], ['type']);
  return failRichText(state, issue === 'structure_override' ? issue : 'invalid_slot');
}

/**
 * Website rich text is the link-free v1 grammar: paragraphs and lists of
 * bold/italic text plus soft breaks. Every text node passes A10 text safety.
 */
export function validateWebsiteRichText(
  value: unknown,
  maxLength: number,
):
  | { readonly ok: true; readonly doc: WebsiteRichTextDocument; readonly length: number }
  | { readonly ok: false; readonly code: WebsiteIssueCode } {
  if (!isRecord(value)) return { ok: false, code: 'invalid_slot' };
  const issue = checkClosedKeys(value, ['type', 'version', 'children']);
  if (issue) return { ok: false, code: issue };
  if (value.type !== 'doc' || value.version !== 1) return { ok: false, code: 'invalid_slot' };
  if (
    !Array.isArray(value.children) ||
    value.children.length === 0 ||
    value.children.length > WEBSITE_RICH_TEXT_BLOCK_LIMIT
  ) {
    return { ok: false, code: 'invalid_slot' };
  }

  const state: RichTextState = { length: 0, visible: false, code: null };
  const children: UnknownRecord[] = [];
  for (const block of value.children) {
    const node = validateRichTextBlock(block, state);
    if (!node) return { ok: false, code: state.code ?? 'invalid_slot' };
    children.push(node);
  }
  if (!state.visible) return { ok: false, code: 'invalid_slot' };
  if (state.length > maxLength) return { ok: false, code: 'text_too_long' };
  return {
    ok: true,
    doc: { type: 'doc', version: 1, children } as unknown as WebsiteRichTextDocument,
    length: state.length,
  };
}

export function validateWebsiteLinkValue(
  value: unknown,
  linkTargets: readonly string[],
):
  | { readonly ok: true; readonly value: WebsiteLinkValue }
  | { readonly ok: false; readonly code: WebsiteIssueCode } {
  if (!isRecord(value)) return { ok: false, code: 'invalid_slot' };
  const kind = value.kind;
  const closed = (keys: readonly string[]): WebsiteIssueCode | null => checkClosedKeys(value, keys);

  if (kind === 'none') {
    const issue = closed(['kind']);
    return issue ? { ok: false, code: issue } : { ok: true, value: { kind: 'none' } };
  }
  if (typeof kind !== 'string' || !linkTargets.includes(kind)) {
    const issue = checkClosedKeys(
      value,
      Object.keys(value).filter((key) => !isStructuralKey(key)),
    );
    return { ok: false, code: issue ?? 'invalid_slot' };
  }
  if (kind === 'category') {
    const issue = closed(['kind', 'categoryId']);
    if (issue) return { ok: false, code: issue };
    return typeof value.categoryId === 'string' && WEBSITE_UUID_RE.test(value.categoryId)
      ? { ok: true, value: { kind, categoryId: value.categoryId } }
      : { ok: false, code: 'invalid_reference' };
  }
  if (kind === 'product') {
    const issue = closed(['kind', 'productId']);
    if (issue) return { ok: false, code: issue };
    return typeof value.productId === 'string' && WEBSITE_UUID_RE.test(value.productId)
      ? { ok: true, value: { kind, productId: value.productId } }
      : { ok: false, code: 'invalid_reference' };
  }
  if (kind === 'custom-collection') {
    const issue = closed(['kind', 'collectionRef']);
    if (issue) return { ok: false, code: issue };
    return typeof value.collectionRef === 'string' &&
      WEBSITE_COLLECTION_REF_RE.test(value.collectionRef)
      ? { ok: true, value: { kind, collectionRef: value.collectionRef } }
      : { ok: false, code: 'invalid_reference' };
  }
  if (kind === 'store-page') {
    const issue = closed(['kind', 'page']);
    if (issue) return { ok: false, code: issue };
    return isOneOf(WEBSITE_STORE_PAGE_LINKS, value.page)
      ? { ok: true, value: { kind, page: value.page } }
      : { ok: false, code: 'invalid_slot' };
  }
  if (kind === 'contact') {
    const issue = closed(['kind', 'method']);
    if (issue) return { ok: false, code: issue };
    return isOneOf(WEBSITE_CONTACT_METHODS, value.method)
      ? { ok: true, value: { kind, method: value.method } }
      : { ok: false, code: 'invalid_slot' };
  }
  if (kind === 'external') {
    // Epic #228 P5 (PD2): the closed keys first, so `url` (a structural key
    // anywhere else) is legal here and only here. The URL is kept byte for
    // byte: the rule is a predicate, never a rewrite (PD1).
    const issue = closed(['kind', 'url']);
    if (issue) return { ok: false, code: issue };
    return isAllowedSocialUrl(value.url)
      ? { ok: true, value: { kind, url: value.url } }
      : { ok: false, code: 'invalid_slot' };
  }
  return { ok: false, code: 'invalid_slot' };
}

function validateMediaValue(schema: WebsiteSlotSchema, value: unknown): SlotValidation {
  if (!isRecord(value)) return fail('invalid_slot');
  const issue = checkClosedKeys(
    value,
    ['mediaAssetId', 'kind', 'posterAssetId', 'alt', 'framing'],
    ['mediaAssetId', 'kind', 'posterAssetId', 'alt'],
  );
  if (issue) return fail(issue);
  // Epic #228 P6: framing is checked right after the closed keys, before any
  // other media rule, so a value with two defects reports the framing one.
  // SQL `website_validate_slot_value` checks in the same order (fixture
  // `media-framing.json`, row "framing outranks a bad media id").
  const hasFraming = Object.prototype.hasOwnProperty.call(value, 'framing');
  if (hasFraming) {
    const framingIssue = validateWebsiteMediaFraming(value.framing);
    if (framingIssue) return fail(framingIssue);
  }
  if (typeof value.mediaAssetId !== 'string' || !WEBSITE_UUID_RE.test(value.mediaAssetId)) {
    return fail('invalid_media');
  }
  if (!isOneOf(WEBSITE_MEDIA_KINDS, value.kind)) return fail('invalid_media');
  if (!(schema.mediaKinds ?? []).includes(value.kind)) return fail('media_kind_not_allowed');

  const poster = value.posterAssetId;
  if (poster !== null && (typeof poster !== 'string' || !WEBSITE_UUID_RE.test(poster))) {
    return fail('invalid_media');
  }
  if (value.kind !== 'image' && poster === null) return fail('poster_required');
  // A still image is its own poster; a separate one would be dead data.
  if (value.kind === 'image' && poster !== null) return fail('invalid_media');
  if (poster !== null && poster.toLowerCase() === value.mediaAssetId.toLowerCase()) {
    return fail('invalid_media');
  }

  const alt = checkMerchantText(value.alt, {
    maxLength: WEBSITE_MEDIA_ALT_MAX_LENGTH,
    allowEmpty: schema.decorative === true,
  });
  if (!alt.ok) return fail(alt.code);

  const media: WebsiteMediaValue = {
    mediaAssetId: value.mediaAssetId,
    kind: value.kind,
    posterAssetId: poster,
    alt: value.alt as string,
    // Copied verbatim (already validated); never `framing: undefined`.
    ...(hasFraming ? { framing: value.framing as WebsiteMediaFraming } : {}),
  };
  return { ok: true, value: { v: media } };
}

/**
 * Pure (shape/limits) validation of one slot value against its schema.
 * Same-store existence of media and references is proven only by SQL.
 */
export function validateSlotValue(schema: WebsiteSlotSchema, value: unknown): SlotValidation {
  if (!isRecord(value)) return fail('invalid_slot');

  const issue = checkClosedKeys(value, ['v', 'empty'], []);
  if (issue) return fail(issue);
  const hasValue = Object.prototype.hasOwnProperty.call(value, 'v');
  const hasEmpty = Object.prototype.hasOwnProperty.call(value, 'empty');
  if (hasValue === hasEmpty) return fail('invalid_slot');

  if (hasEmpty) {
    if (value.empty !== true) return fail('invalid_slot');
    return schema.required ? fail('missing_required') : { ok: true, value: { empty: true } };
  }

  const inner = value.v;
  switch (schema.type) {
    case 'newsletter-collection': {
      if (!isRecord(inner)) return fail('invalid_slot');
      const collectionIssue = checkClosedKeys(inner, [
        'enabled',
        'contactMethods',
        'consentVersion',
      ]);
      if (collectionIssue) return fail(collectionIssue);
      const collection = sanitizeStorefrontNewsletterCollectionConfig(inner);
      return collection ? { ok: true, value: { v: collection } } : fail('invalid_slot');
    }
    case 'text': {
      const text = checkMerchantText(inner, {
        maxLength: schema.maxLength ?? 0,
        multiline: schema.multiline === true,
      });
      return text.ok ? { ok: true, value: { v: inner as string } } : fail(text.code);
    }
    case 'rich-text': {
      const doc = validateWebsiteRichText(inner, schema.maxLength ?? 0);
      return doc.ok ? { ok: true, value: { v: doc.doc } } : fail(doc.code);
    }
    case 'link': {
      // Epic #228 P5 (N7): the platform rule. Every link slot admits
      // `external` unless its manifest opts out; SQL expands identically.
      const link = validateWebsiteLinkValue(inner, websiteAllowedLinkKinds(schema));
      return link.ok ? { ok: true, value: { v: link.value } } : fail(link.code);
    }
    case 'media':
      return validateMediaValue(schema, inner);
    case 'category-ref': {
      if (!isRecord(inner)) return fail('invalid_slot');
      const refIssue = checkClosedKeys(inner, ['categoryId']);
      if (refIssue) return fail(refIssue);
      return typeof inner.categoryId === 'string' && WEBSITE_UUID_RE.test(inner.categoryId)
        ? { ok: true, value: { v: { categoryId: inner.categoryId } } }
        : fail('invalid_reference');
    }
    case 'product-ref': {
      if (!isRecord(inner)) return fail('invalid_slot');
      const refIssue = checkClosedKeys(inner, ['productId']);
      if (refIssue) return fail(refIssue);
      return typeof inner.productId === 'string' && WEBSITE_UUID_RE.test(inner.productId)
        ? { ok: true, value: { v: { productId: inner.productId } } }
        : fail('invalid_reference');
    }
    case 'custom-collection-ref': {
      if (!isRecord(inner)) return fail('invalid_slot');
      const refIssue = checkClosedKeys(inner, ['collectionRef']);
      if (refIssue) return fail(refIssue);
      return typeof inner.collectionRef === 'string' &&
        WEBSITE_COLLECTION_REF_RE.test(inner.collectionRef)
        ? { ok: true, value: { v: { collectionRef: inner.collectionRef } } }
        : fail('invalid_reference');
    }
    default:
      return fail('invalid_slot');
  }
}

export interface StoreWebsiteDocumentIssue {
  readonly code: WebsiteIssueCode;
  readonly slotId?: string;
}

export type StoreWebsiteDocumentParse =
  | { readonly ok: true; readonly document: StoreWebsiteDocument }
  | { readonly ok: false; readonly issues: readonly StoreWebsiteDocumentIssue[] };

/**
 * Parses a whole store document against the pinned manifest. Structure is
 * derived from the manifest only: unknown slots and structural keys are
 * rejected, never ignored.
 */
export function parseStoreWebsiteDocument(
  manifest: WebsiteTemplateManifest,
  json: unknown,
): StoreWebsiteDocumentParse {
  if (!isRecord(json)) return { ok: false, issues: [{ code: 'invalid_slot' }] };

  let serialized: string;
  try {
    serialized = JSON.stringify(json);
  } catch {
    return { ok: false, issues: [{ code: 'invalid_slot' }] };
  }
  if (utf8ByteLength(serialized) > WEBSITE_DOCUMENT_MAX_BYTES) {
    return { ok: false, issues: [{ code: 'document_too_large' }] };
  }

  const topIssue = checkClosedKeys(json, [
    'templateVersionId',
    'slots',
    'theme',
    'interfaceLocale',
  ]);
  if (topIssue) return { ok: false, issues: [{ code: topIssue }] };

  const issues: StoreWebsiteDocumentIssue[] = [];
  if (typeof json.templateVersionId !== 'string' || !WEBSITE_UUID_RE.test(json.templateVersionId)) {
    issues.push({ code: 'template_unavailable' });
  }
  if (!isOneOf(WEBSITE_INTERFACE_LOCALES, json.interfaceLocale)) {
    issues.push({ code: 'invalid_slot' });
  }

  const schemaById = new Map(manifest.slots.map((slot) => [slot.id, slot]));
  const slots: Record<string, WebsiteSlotValue> = {};
  if (!isRecord(json.slots)) {
    issues.push({ code: 'invalid_slot' });
  } else {
    for (const [slotId, raw] of Object.entries(json.slots)) {
      const schema = schemaById.get(slotId);
      if (!schema) {
        issues.push({
          code: isStructuralKey(slotId) ? 'structure_override' : 'unknown_slot',
          slotId,
        });
        continue;
      }
      const result = validateSlotValue(schema, raw);
      if (result.ok) slots[slotId] = result.value;
      else issues.push({ code: result.code, slotId });
    }
  }

  let theme: WebsiteThemeOverride = {};
  const themeResult = validateThemeOverride(manifest.theme, json.theme);
  if (themeResult.ok) theme = themeResult.override;
  else issues.push(...themeResult.issues.map((code) => ({ code })));

  if (issues.length > 0) return { ok: false, issues };
  return {
    ok: true,
    document: {
      templateVersionId: json.templateVersionId as string,
      slots,
      theme,
      interfaceLocale: json.interfaceLocale as StoreWebsiteDocument['interfaceLocale'],
    },
  };
}

/** C4: one Save of one region dialog. */
export const WEBSITE_SLOT_BATCH_MAX = 12;

/** Keyed by slot id. `null` = use the template default again. */
export type WebsiteSlotBatch = Readonly<Record<string, WebsiteSlotValue | null>>;

export type WebsiteSlotBatchCheck =
  | { readonly ok: true }
  | { readonly ok: false; readonly code: WebsiteIssueCode; readonly slotId: string | null };

/** Code point order, which is the byte order SQL uses (`COLLATE "C"`). */
function compareCodePoints(left: string, right: string): number {
  const a = [...left];
  const b = [...right];
  const shared = Math.min(a.length, b.length);
  for (let index = 0; index < shared; index += 1) {
    const difference = (a[index]?.codePointAt(0) ?? 0) - (b[index]?.codePointAt(0) ?? 0);
    if (difference !== 0) return difference;
  }
  return a.length - b.length;
}

function isSlotIdShaped(key: string): boolean {
  return key.length <= WEBSITE_SLOT_ID_MAX_LENGTH && WEBSITE_SLOT_ID_RE.test(key);
}

/**
 * Pure check of a batch against the pinned manifest. It reports the FIRST
 * issue in the order `save_store_website_slots` finds them: the shape of the
 * batch, then keys the manifest does not define (sorted), then the values in
 * manifest order. Same-store existence of media and references is proven
 * only by SQL.
 */
export function validateWebsiteSlotBatch(
  manifest: WebsiteTemplateManifest,
  batch: WebsiteSlotBatch,
): WebsiteSlotBatchCheck {
  // Callers hand over form state; the type alone proves nothing at runtime.
  const raw: unknown = batch;
  if (!isRecord(raw)) return { ok: false, code: 'invalid_slot', slotId: null };
  const keys = Object.keys(raw);
  if (keys.length === 0 || keys.length > WEBSITE_SLOT_BATCH_MAX) {
    return { ok: false, code: 'invalid_slot', slotId: null };
  }

  const position = new Map(manifest.slots.map((slot, index) => [slot.id, index]));
  const unknown = keys.filter((key) => !position.has(key)).sort(compareCodePoints);
  const first = unknown[0];
  if (first !== undefined) {
    return {
      ok: false,
      code: isStructuralKey(first) ? 'structure_override' : 'unknown_slot',
      slotId: isSlotIdShaped(first) ? first : null,
    };
  }

  const known = [...keys].sort(
    (left, right) => (position.get(left) ?? 0) - (position.get(right) ?? 0),
  );
  for (const slotId of known) {
    const value = raw[slotId];
    if (value === null) continue;
    const schema = manifest.slots[position.get(slotId) ?? -1];
    if (!schema) continue;
    const result = validateSlotValue(schema, value);
    if (!result.ok) return { ok: false, code: result.code, slotId };
  }
  return { ok: true };
}

/**
 * Epic #228 P4: deep equality of two stored slot values for the save queue's
 * rebase check. Absent, `undefined` and `null` all mean "the template
 * default" and are equal to each other; `{ empty: true }` is a stored choice
 * and is NOT the default. Key order never matters; array order does.
 */
export function websiteSlotValuesEqual(
  a: WebsiteSlotValue | null | undefined,
  b: WebsiteSlotValue | null | undefined,
): boolean {
  const left = a ?? null;
  const right = b ?? null;
  if (left === null || right === null) return left === right;
  return stableSlotJson(left) === stableSlotJson(right);
}

/** Epic #228 P4 (PRD §11.2): how soon an edit of one slot is sent. */
export type WebsiteSlotCommitMode = 'debounce' | 'immediate';

function isAuthoredMedia(value: WebsiteSlotValue | null | undefined): value is {
  readonly v: WebsiteMediaValue;
} {
  if (value === null || value === undefined || !('v' in value)) return false;
  const inner: unknown = value.v;
  return isRecord(inner) && typeof inner.mediaAssetId === 'string' && typeof inner.alt === 'string';
}

function isAuthoredExternal(value: WebsiteSlotValue | null | undefined): boolean {
  if (value === null || value === undefined || !('v' in value)) return false;
  const inner: unknown = value.v;
  return isRecord(inner) && inner.kind === 'external';
}

/**
 * Epic #228 P4 (PD3): typing is debounced; a choice is sent at once.
 *
 * - `text` / `rich-text`: a new authored value is typing → `debounce`. A
 *   switch to the template default (`null`) or "leave empty" is a choice.
 * - `media`: when only the description (`alt`) changed → `debounce` (the
 *   merchant is typing it); a new file, a new cover, a removal or a new
 *   framing (epic #228 P6: an Apply in the crop editor) → `immediate`.
 * - `link`: typing an Instagram/Facebook URL (both values `external`) →
 *   `debounce` (epic #228 P5, PD5); a switch of kind or target → `immediate`.
 * - Everything else (references, newsletter settings) → `immediate`.
 */
export function websiteSlotCommitMode(
  schema: WebsiteSlotSchema,
  previous: WebsiteSlotValue | null | undefined,
  next: WebsiteSlotValue | null | undefined,
): WebsiteSlotCommitMode {
  if (schema.type === 'text' || schema.type === 'rich-text') {
    return next !== null && next !== undefined && 'v' in next ? 'debounce' : 'immediate';
  }
  if (schema.type === 'media' && isAuthoredMedia(previous) && isAuthoredMedia(next)) {
    const before = previous.v;
    const after = next.v;
    const sameFile =
      before.mediaAssetId === after.mediaAssetId &&
      before.kind === after.kind &&
      before.posterAssetId === after.posterAssetId &&
      stableSlotJson(before.framing ?? null) === stableSlotJson(after.framing ?? null);
    return sameFile && before.alt !== after.alt ? 'debounce' : 'immediate';
  }
  if (schema.type === 'link' && isAuthoredExternal(previous) && isAuthoredExternal(next)) {
    return 'debounce';
  }
  return 'immediate';
}
